import React, { useState } from 'react';
import { Upload, Plus, AlertCircle, Check } from 'lucide-react';

/**
 * Review Import Component
 * Allows users to import reviews via CSV or manual entry
 * Fallback when Facebook API has issues
 */
function ReviewImporter({ onReviewsImported, onClose }) {
  const [importMode, setImportMode] = useState('csv'); // 'csv' or 'manual'
  const [csvContent, setCsvContent] = useState('');
  const [manualReviews, setManualReviews] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importedCount, setImportedCount] = useState(0);

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setCsvContent(event.target.result);
    };
    reader.readAsText(file);
  };

  const parseCSV = (content) => {
    const lines = content.trim().split('\n');
    const reviews = [];

    // Expected format: rating,reviewer_name,review_text
    for (let i = 1; i < lines.length; i++) {
      const [rating, name, text] = lines[i].split(',');
      if (rating && text) {
        const id = `manual_${Date.now()}_${i}`;
        reviews.push({
          id,
          rating: parseInt(rating) || 5,
          reviewer_name: name || 'Anonymous',
          text,
          published_at: new Date().toISOString(),
          platform_source: 'manual',
          facebook_review_id: id,
          has_reply: false
        });
      }
    }

    return reviews;
  };

  const handleImportCSV = async () => {
    if (!csvContent.trim()) {
      setError('Please select a CSV file');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const reviews = parseCSV(csvContent);
      if (reviews.length === 0) {
        setError('No valid reviews found in CSV. Expected format: rating,name,review_text');
        setLoading(false);
        return;
      }

      setImportedCount(reviews.length);
      onReviewsImported(reviews);
      
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImportManual = async () => {
    if (!manualReviews.trim()) {
      setError('Please enter at least one review');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Parse manual reviews (one per line: rating|name|text)
      const lines = manualReviews.trim().split('\n');
      const reviews = [];

      for (let i = 0; i < lines.length; i++) {
        const [rating, name, text] = lines[i].split('|').map(s => s.trim());
        if (text) {
          const id = `manual_${Date.now()}_${i}`;
          reviews.push({
            id,
            rating: parseInt(rating) || 5,
            reviewer_name: name || 'Anonymous',
            text,
            published_at: new Date().toISOString(),
            platform_source: 'manual',
            facebook_review_id: id,
            has_reply: false
          });
        }
      }

      if (reviews.length === 0) {
        setError('No valid reviews found. Format: rating|name|text (one per line)');
        setLoading(false);
        return;
      }

      setImportedCount(reviews.length);
      onReviewsImported(reviews);

      setTimeout(() => onClose(), 2000);
    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-purple-500/30 rounded-2xl shadow-2xl max-w-2xl w-full p-8">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
          <Upload className="text-pink-400" />
          Import Reviews
        </h2>

        {/* Mode Selector */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setImportMode('csv')}
            className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
              importMode === 'csv'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                : 'bg-slate-700/50 text-purple-200 hover:bg-slate-700'
            }`}
          >
            📄 CSV File
          </button>
          <button
            onClick={() => setImportMode('manual')}
            className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
              importMode === 'manual'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                : 'bg-slate-700/50 text-purple-200 hover:bg-slate-700'
            }`}
          >
            ✍️ Manual Entry
          </button>
        </div>

        {/* CSV Mode */}
        {importMode === 'csv' && (
          <div className="space-y-4 mb-6">
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <p className="text-sm text-blue-200 mb-2">📋 CSV Format:</p>
              <code className="text-xs bg-slate-900 p-2 rounded block text-slate-300 overflow-x-auto">
                rating,reviewer_name,review_text<br/>
                5,John Doe,Amazing service!<br/>
                3,Jane Smith,Good but could improve
              </code>
            </div>

            <div className="border-2 border-dashed border-purple-500/30 rounded-lg p-6 text-center hover:border-purple-500/60 transition cursor-pointer">
              <input
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer block">
                <Upload className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <p className="text-purple-200 font-semibold">Choose CSV file</p>
                <p className="text-purple-300/60 text-sm">or drag and drop</p>
              </label>
            </div>

            {csvContent && (
              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3">
                <p className="text-green-200 text-sm">✓ File ready to import</p>
              </div>
            )}
          </div>
        )}

        {/* Manual Entry Mode */}
        {importMode === 'manual' && (
          <div className="space-y-4 mb-6">
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <p className="text-sm text-blue-200 mb-2">📋 Format (one per line):</p>
              <code className="text-xs bg-slate-900 p-2 rounded block text-slate-300">
                rating | name | review text
              </code>
            </div>

            <textarea
              value={manualReviews}
              onChange={(e) => setManualReviews(e.target.value)}
              placeholder="5|John|Great experience!&#10;3|Jane|Needs improvement"
              className="w-full h-32 bg-slate-700/50 border border-purple-500/30 rounded-lg px-4 py-3 text-purple-100 placeholder-purple-400/50 focus:outline-none focus:border-purple-500/60"
            />
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {importedCount > 0 && (
          <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg flex gap-3 animate-in">
            <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-green-200 text-sm">✓ Successfully imported {importedCount} reviews!</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-purple-200 border border-purple-500/30 rounded-lg hover:bg-slate-700/50 transition"
          >
            Cancel
          </button>
          <button
            onClick={importMode === 'csv' ? handleImportCSV : handleImportManual}
            disabled={loading || (importMode === 'csv' ? !csvContent : !manualReviews)}
            className={`flex-1 py-2.5 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              importMode === 'csv' && !csvContent
                ? 'bg-slate-700/50 text-purple-300/50 cursor-not-allowed'
                : loading
                ? 'bg-purple-600/50 text-purple-200 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
            }`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-transparent border-t-white" />
                Importing...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Import Reviews
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReviewImporter;
